import React, { useMemo, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import debounce from 'debounce-promise';
import { OverlayTriggerHandle } from 'rsuite/esm/internals/Overlay/OverlayTrigger';

import { ActiveFilter, SuperGridFilters, useUrlParams } from '@aleph/shared-components';

import {
	getFilterConfig,
	LEVEL_NAME_FILTER,
	LEVEL_ID_FILTER,
} from 'wb-reddit/app/routes/superGrid/actionRow/filters/utils';
import ClientOptionsQuery from 'wb-reddit/app/routes/superGrid/actionRow/filters/api/clientOptions.graphql';
import CampaignOptionsQuery from 'wb-reddit/app/routes/superGrid/actionRow/filters/api/campaignOptions.graphql';
import GroupOptionsQuery from 'wb-reddit/app/routes/superGrid/actionRow/filters/api/groupOptions.graphql';
import AdOptionsQuery from 'wb-reddit/app/routes/superGrid/actionRow/filters/api/adOptions.graphql';
import { RedditLevel } from 'wb-reddit/app/routes/superGrid/utils';
import { motion } from 'framer-motion';
import { AdvertisersOptionsDocument, UserGroupOptionsDocument, UserOptionsDocument } from 'wb-reddit/graphql/generated';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import { useNavigate, useParams } from 'react-router-dom';
import { captureException } from '@sentry/react';

const FILTER_OPTION_QUERIES = {
	[RedditLevel.AD_ACCOUNT]: ClientOptionsQuery,
	[RedditLevel.CAMPAIGN]:   CampaignOptionsQuery,
	[RedditLevel.GROUP]:      GroupOptionsQuery,
	[RedditLevel.AD]:         AdOptionsQuery,
};

const RedditFiltersContainer = ({
	filtersOpen,
	setFiltersOpen,
	filterButtonPos,
}) => {
	const filterPopoverRef = useRef<OverlayTriggerHandle | null>(null);
	const navigate = useNavigate();
	const { params, setUrlParam } = useUrlParams({ navigate, useParams });
	const client = useApolloClient();
	const platformUser = useUserMetaData();

	const getCurrentFilters = (filterValue: string) => params.filters?.[filterValue]?.[0]?.value ?? [];

	const getEntityOptions = async (filterName: string, search: string, level: RedditLevel) => {
		let optionsQuery = FILTER_OPTION_QUERIES[level];
		let variables: undefined | object;

		if (filterName === 'userGroupId') {
			optionsQuery = UserGroupOptionsDocument;
			variables = {
				filters: undefined,
				userId:  platformUser?.userId ?? '',
				search:  `*${search}*`,
			};
		}

		if (filterName === 'agencyAccountId') {
			optionsQuery = AdvertisersOptionsDocument;
			variables = {
				filters: undefined,
				search:  `*${search}*`,
			};
		}

		if (filterName === 'userId') {
			optionsQuery = UserOptionsDocument;
			variables = {
				filters: undefined,
				search:  `userFullName LIKE '%${search}%' OR userEmail LIKE '%${search}%'`,
			};
		}

		try {
			const { data } = await client.query({
				query:     optionsQuery,
				variables: {
					filters: {
						...params?.filters,
						[LEVEL_ID_FILTER[level]]:   undefined,
						[LEVEL_NAME_FILTER[level]]: [{ wildcard: true, value: [`*${search}*`] }],
					},
					...variables,
				},
			});

			return data?.options ?? [];
		} catch (e) {
			captureException(e);

			return { items: [] };
		}
	};

	const debouncedGetEntityOptions = useMemo(
		() => debounce(getEntityOptions, 300),
		[JSON.stringify(params?.filters ?? {})],
	);

	const closeFilter = () => {
		if (!filtersOpen) {
			setFiltersOpen(false);
		}
	};

	const setFilters = (filterName: string, filterValue: string[]) => {
		setUrlParam('filters', {
			...params?.filters,
			[filterName]: filterValue?.length > 0 ? [{ value: filterValue }] : undefined,
		});
		filterPopoverRef?.current?.close();
	};

	const FILTERS_CONFIG = getFilterConfig(
		setFilters,
		getCurrentFilters,
		debouncedGetEntityOptions,
		platformUser?.isAdmin,
		params.level,
	);

	const activeFilters = (
		<div
			className='flex gap-[8px]'
		>
			{FILTERS_CONFIG.map((filter, idx) => (
				Object.keys(params.filters)?.find(urlFilter => urlFilter === filter.value)
					? (
						<ActiveFilter
							key={idx}
							filterPopoverRef={filterPopoverRef}
							className='rounded-full bg-white text-black hover:bg-gray
								text-[11px] font-semibold'
							urlParams={params}
							filter={filter.value}
							unsetFilter={() => setFilters(filter.value, [])}
							filterComponent={(
								<filter.component
									activeFilters={getCurrentFilters(filter.value)}
									{...filter.componentProps}
								/>
							)}
							filterLabel={(
								<div className="uppercase">
									{filter.label}
								</div>
							)}
							showCount={true}
						/>
					) : null
			))}
		</div>
	);

	return (
		<motion.div
			transition={{ duration: 0.1 }}
			initial={{ height: 0 }}
			animate={{ height: filtersOpen ? 40 : 0 }}
			exit={{ height: 0 }}
		>
			{filtersOpen ? (
				<SuperGridFilters
					openMenu={filtersOpen}
					getCurrentFilters={getCurrentFilters}
					filtersConfiguration={FILTERS_CONFIG}
					ActiveFilters={activeFilters}
					closeFilter={closeFilter}
					filterButtonPos={filterButtonPos}
				/>) : null}
		</motion.div>
	);
};

export default RedditFiltersContainer;
